import { Store } from '@ngxs/store';
import { IanTranslateService } from 'core/services/ian-core-singleton.service';
import { DevSettingsService, FeatureFlagsKeysEnum } from 'core/services/dev-settings.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppInjector } from 'app/app-injector';
import { EditShelfProdsBatch } from '../planograms-editor/store/planogram.store.state';
import {
  getLinearWidthPerFacing,
  getProdTotalLinearWidth,
  getShelfRemainingWidth,
  ProductCompressionAttemptParams,
  ProductCompressionAttemptResult,
  ProductCompressionChangeType,
  tryShelfProductCompression,
} from './spaces_product_compression';

/** CATMAN-S12 (CAT-1793) — OFF por defecto hasta activación por tenant en AWS. */
export const PRODUCT_COMPRESSION_FEATURE_FLAG = FeatureFlagsKeysEnum['FeatureFlag__CAT-1793'];

export const isProductCompressionFeatureEnabled = (configFlags?: DevSettingsService | null): boolean =>
  !!configFlags?.hasFeatureFlag(PRODUCT_COMPRESSION_FEATURE_FLAG);

export const isProductCompressionFeatureEnabledFromInjector = (): boolean =>
  isProductCompressionFeatureEnabled(AppInjector.get(DevSettingsService));

export const applyCompressionToFacingWidth = (
  baseWidth: number,
  prod: any,
  _enabled = isProductCompressionFeatureEnabledFromInjector()
): number => {
  const percent = Number(prod?.appliedCompressionPercent);
  // Si el producto tiene compresión persistida, el render siempre usa el ancho efectivo (CATMAN-S54).
  if (!(percent > 0)) return baseWidth;
  return baseWidth * (1 - percent / 100);
};

/** Ancho lineal por frente con compresión aplicada (CATMAN-S54). */
export const getEffectiveLinearWidthPerFacing = (prod: any, enabled = isProductCompressionFeatureEnabledFromInjector()): number => {
  const percent = Number(prod?.appliedCompressionPercent);
  if (percent > 0) return applyCompressionToFacingWidth(getLinearWidthPerFacing(prod), prod);
  if (!enabled) return getLinearWidthPerFacing(prod);
  return applyCompressionToFacingWidth(getLinearWidthPerFacing(prod), prod);
};

/** Ancho lineal total del producto en el estante con compresión aplicada (CATMAN-S54). */
export const getEffectiveProdTotalLinearWidth = (prod: any, enabled = isProductCompressionFeatureEnabledFromInjector()): number => {
  if (!enabled) return getProdTotalLinearWidth(prod, null);

  const percent =
    prod?.appliedCompressionPercent != null && prod.appliedCompressionPercent > 0 ? Number(prod.appliedCompressionPercent) : null;

  return getProdTotalLinearWidth(prod, percent);
};

export const hasShelfActiveCompression = (prods: any[]): boolean =>
  (prods || []).some(p => p?.appliedCompressionPercent != null && Number(p.appliedCompressionPercent) > 0);

/** Espacio lineal libre del estante; nunca negativo cuando la compresión está activa (CATMAN-S54). */
export const getShelfRemainingLinearWidth = (
  prods: any[],
  shelfWidth: number,
  enabled = isProductCompressionFeatureEnabledFromInjector()
): number => {
  if (!enabled) return shelfWidth;

  const remaining = getShelfRemainingWidth(shelfWidth, prods || [], null);
  return hasShelfActiveCompression(prods) ? Math.max(0, remaining) : remaining;
};

export const getShelfWidthForCompression = (parentShelf: any): number => {
  const shelfData = parentShelf?.objDataShelf || {};
  const marginLeft = parentShelf?.marginLeft || shelfData.marginLeft || 0;
  const marginRight = parentShelf?.marginRight || shelfData.marginRight || 0;
  const width = Number(shelfData.width || parentShelf?.size?.w || 0);
  return Math.max(0, width - marginLeft - marginRight);
};

export const runShelfCompressionAttempt = (
  parentShelf: any,
  params: Omit<ProductCompressionAttemptParams, 'shelfProds' | 'shelfWidth' | 'shelfType'>
): ProductCompressionAttemptResult => {
  return tryShelfProductCompression({
    ...params,
    shelfProds: parentShelf?.prods || [],
    shelfWidth: getShelfWidthForCompression(parentShelf),
    shelfType: parentShelf?.type,
  });
};

export const dispatchShelfCompressionResult = (store: Store, result: ProductCompressionAttemptResult): boolean => {
  if (!result.ok || !result.updates?.length) return false;

  store.dispatch(
    new EditShelfProdsBatch(
      result.updates.map(u => ({
        prodId: u.prodId,
        props: {
          appliedCompressionPercent: u.appliedCompressionPercent,
          appliedCompressionAxis: u.appliedCompressionAxis,
          ...(u.facings != null ? { facings: u.facings } : {}),
          ...(u.rotation != null ? { rotation: u.rotation } : {}),
          ...(u.rotateY != null ? { rotateY: u.rotateY } : {}),
          ...(u.stacksLyingDown != null ? { stacksLyingDown: u.stacksLyingDown } : {}),
        },
      }))
    )
  );

  return true;
};

export const notifyCompressionRejected = (
  snackBar: MatSnackBar,
  translate: IanTranslateService,
  result: ProductCompressionAttemptResult,
  changeType: ProductCompressionChangeType
) => {
  const blocking = result.blockingProductNames?.filter(Boolean) || [];
  let messageKey = 'SPACES.EDITOR.MSG_PRD_NO_ENOUGH_SPACE';

  if (blocking.length) {
    messageKey = 'SPACES.EDITOR.MSG_COMPRESSION_BLOCKED';
  } else if (changeType === 'orientation_change') {
    messageKey = 'SPACES.EDITOR.MSG_COMPRESSION_ORIENTATION_REJECTED';
  } else if (changeType === 'add_facing') {
    messageKey = 'SPACES.EDITOR.MSG_COMPRESSION_ADD_FACING_REJECTED';
  } else if (changeType === 'add_product') {
    messageKey = 'SPACES.EDITOR.MSG_COMPRESSION_ADD_PRODUCT_REJECTED';
  }

  const message =
    blocking.length > 0
      ? translate.instant(messageKey, blocking.join(', '), 'No hay espacio suficiente')
      : translate.instant(messageKey, null, 'No hay espacio suficiente');

  snackBar.open(message, translate.instant('SPACES.EDITOR.BTN_ACEPT', null, 'Aceptar'), { duration: 4000 });
};

export const notifyCompressionApplied = (snackBar: MatSnackBar, translate: IanTranslateService, percent: number) => {
  snackBar.open(
    translate.instant('SPACES.EDITOR.MSG_COMPRESSION_APPLIED', Math.round(percent * 10) / 10, 'Compresión aplicada'),
    translate.instant('SPACES.EDITOR.BTN_ACEPT', null, 'Aceptar'),
    { duration: 3000 }
  );
};
